/* Hook for searching movies via OMDb API through Convex actions. */

import { useState, useCallback } from "react";
import { useAction } from "convex/react";
import { api } from "../../convex/_generated/api";

export interface MovieSearchResult {
  title: string;
  year: string;
  imdbId: string;
  type: string;
  poster: string | null;
}

export interface MovieDetails {
  title: string;
  year: string;
  imdbId: string;
  director: string;
  actors: string;
  plot: string;
  genres: string;
  runtime: string;
  rating: string;
  poster: string | null;
}

export function useMovieSearch() {
  const [results, setResults] = useState<MovieSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchAction = useAction(api.movies.searchMovies);
  const detailsAction = useAction(api.movies.getMovieDetails);

  const search = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const data = await searchAction({ query: trimmed });
      setResults(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Search failed";
      setError(message);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchAction]);

  const fetchDetails = useCallback(async (imdbId: string): Promise<MovieDetails> => {
    return await detailsAction({ imdbId });
  }, [detailsAction]);

  return { results, isSearching, error, search, fetchDetails };
}
